import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useBranding } from "@/hooks/useBranding";

const VALUES = [
  {
    title: "Clinical Impact",
    description: "Solve real-world healthcare friction. Every line of code and every customer interaction directly improves patient outcomes and reduces administrative burnout.",
    icon: (
      <div className="w-10 h-10 bg-[#d1e7d1] rounded-lg flex items-center justify-center">
        <svg className="w-6 h-6 text-[#1a3c34]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      </div>
    ),
  },
  {
    title: "Innovation First",
    description: "Work with cutting-edge AI and real-time data analytics. We don't just follow industry standards; we set them for the next decade of digital health.",
    icon: (
      <div className="w-10 h-10 bg-[#fce4ec] rounded-lg flex items-center justify-center">
        <svg className="w-6 h-6 text-[#a55e43]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
    ),
  },
  {
    title: "Wellness Minded",
    description: "We believe high performance requires high care. Enjoy comprehensive health coverage, mental health support, and truly flexible remote-first schedules.",
    icon: (
      <div className="w-10 h-10 bg-[#e1f5fe] rounded-lg flex items-center justify-center">
        <svg className="w-6 h-6 text-[#0288d1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      </div>
    ),
  },
];

const TEAMS = [
  { name: "Engineering & Product", description: "Building the core engine of healthcare flow.", img: "https://images.unsplash.com/photo-1522071823991-b99c123adbb0?auto=format&fit=crop&q=80&w=800" },
  { name: "Clinical Operations", description: "Bridging the gap between code and care.", img: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=800" },
  { name: "Customer Success", description: "Ensuring every partner reaches peak efficiency.", img: "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&q=80&w=800" },
  { name: "Sales & Growth", description: "Expanding our mission to every clinic worldwide.", img: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800" },
];

const JOBS = [
  { title: "Senior Frontend Engineer (Remote)", dept: "Engineering", location: "Global Remote" },
  { title: "Clinic Implementation Lead (NYC)", dept: "Operations", location: "New York, USA" },
  { title: "Product Designer (Hybrid)", dept: "Product", location: "London, UK" },
];

const BENEFITS = [
  "Premium Health, Vision, and Dental for you and family",
  "Annual $2,500 learning & development stipend",
  "Unlimited PTO with a 2-week minimum recommendation",
  "Work-from-home setup budget ($1,000)",
];

export default function CareersPage() {
  const branding = useBranding();
  return (
    <div className="min-h-screen bg-[#faf9f7] text-[#1a3c34]">
      <Navbar />

      <section className="pt-20 pb-24 px-6 text-center max-w-5xl mx-auto">
        <h1 className="text-4xl md:text-6xl font-bold mb-8 tracking-tight font-display">
          Join the Future of Healthcare Operations
        </h1>
        <p className="text-lg md:text-xl text-[#2d5a45] max-w-3xl mx-auto leading-relaxed mb-12">
          We're building the digital infrastructure for a more compassionate healthcare system.
          Help us eliminate wait times and empower providers with data-driven workflows.
        </p>
        <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
          <button className="px-10 py-4 bg-[#a55e43] text-white font-bold rounded-xl hover:bg-[#8e4f38] transition-all shadow-lg">
            View Openings
          </button>
          <button className="px-10 py-4 border-2 border-[#1a3c34] text-[#1a3c34] font-bold rounded-xl hover:bg-[#1a3c34]/5 transition-all">
            Our Culture
          </button>
        </div>
      </section>

      <section className="px-6 pb-24 max-w-7xl mx-auto">
        <div className="rounded-3xl overflow-hidden shadow-2xl border border-[#dadad8]">
          <img
            src="https://images.unsplash.com/photo-1581056771107-24ca5f033842?auto=format&fit=crop&q=80&w=2000"
            alt="Healthcare innovation hub"
            className="w-full h-[400px] md:h-[600px] object-cover"
          />
        </div>
      </section>

      <section className="px-6 py-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#a55e43] font-bold uppercase tracking-widest text-sm">The {branding.product_name} Edge</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 font-display tracking-tight">Why you'll love it here</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {VALUES.map((val, i) => (
              <div key={i} className="p-8 rounded-2xl bg-[#faf9f7] border border-[#dadad8] hover:shadow-md transition-shadow">
                <div className="mb-6">{val.icon}</div>
                <h3 className="text-xl font-bold mb-4">{val.title}</h3>
                <p className="text-[#2d5a45] leading-relaxed">{val.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-24 max-w-7xl mx-auto">
        <div className="mb-16">
          <h2 className="text-4xl font-bold mb-4 font-display tracking-tight">Our Teams</h2>
          <p className="text-[#2d5a45] text-lg max-w-2xl">
            Our cross-functional teams work in tight loops to deliver complex solutions with clinical precision.
            Find the department where you can make your biggest mark.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {TEAMS.map((team, i) => (
            <div key={i} className="group relative rounded-2xl overflow-hidden aspect-[4/5] shadow-sm">
              <img src={team.img} alt={team.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a3c34] via-[#1a3c34]/20 to-transparent" />
              <div className="absolute bottom-0 p-6 text-white">
                <h3 className="text-xl font-bold mb-2">{team.name}</h3>
                <p className="text-sm text-white/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300">{team.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 py-24 bg-[#f4f4f2]/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 space-y-6 md:space-y-0">
            <div>
              <h2 className="text-3xl font-bold mb-2 font-display tracking-tight">Open Positions</h2>
              <p className="text-[#2d5a45]">Currently 8 positions across 4 departments</p>
            </div>
            <div className="flex space-x-4">
              <select className="bg-white px-4 py-2 rounded-lg border border-[#dadad8] outline-none text-sm font-medium">
                <option>All Departments</option>
              </select>
              <select className="bg-white px-4 py-2 rounded-lg border border-[#dadad8] outline-none text-sm font-medium">
                <option>All Locations</option>
              </select>
            </div>
          </div>

          <div className="space-y-4">
            {JOBS.map((job, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-[#dadad8] flex flex-col md:flex-row items-center justify-between hover:border-[#a55e43] transition-colors group">
                <div className="mb-4 md:mb-0 text-center md:text-left">
                  <h4 className="text-xl font-bold mb-1">{job.title}</h4>
                  <p className="text-sm text-[#2d5a45] font-medium">{job.dept} &middot; {job.location}</p>
                </div>
                <button className="px-8 py-3 border-2 border-[#1a3c34] text-[#1a3c34] font-bold rounded-xl group-hover:bg-[#1a3c34] group-hover:text-white transition-all">
                  Apply Now
                </button>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <button className="text-[#1a3c34] font-bold flex items-center justify-center mx-auto hover:underline">
              Load more positions
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <section className="bg-[#1a3c34] py-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight font-display">
              We invest in the people who build the future.
            </h2>
            <ul className="space-y-6">
              {BENEFITS.map((benefit, i) => (
                <li key={i} className="flex items-start text-white/90">
                  <div className="bg-[#2d5a45] p-1 rounded-full mr-4 mt-1">
                    <svg className="w-4 h-4 text-[#d1e7d1]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-lg font-medium">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "Remote Flexible", value: "100%" },
              { label: "Employee Premiums", value: "$0" },
              { label: "4% Matching", value: "401k" },
              { label: "Parental Leave", value: "16wk" },
            ].map((stat, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/10 p-8 rounded-2xl text-center">
                <div className="text-3xl font-bold text-[#a55e43] mb-2">{stat.value}</div>
                <div className="text-xs font-bold text-white/50 tracking-widest uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <div className="fixed inset-0 z-50 backdrop-blur-[2px] bg-[#1a3c34]/10 flex items-center justify-center pointer-events-none">
        <div className="-rotate-12 bg-[#a55e43] text-white text-4xl md:text-6xl font-bold uppercase tracking-[0.2em] px-12 py-4 shadow-2xl opacity-90">
          Coming soon
        </div>
      </div>
    </div>
  );
}
